'use client'
import {FadeInSlide} from "app/motions/fadeInSlide";
import {DualPricingPaymentTypes} from "app/sales/useSales";
import {AsIconButton, DialogLayout} from "app/subframe";
import {DualButton} from "app/subframe/components/DualButton";
import * as React from "react";
import TipModal from "./TipModal";
import CashTenderedModal from "./CashTenderedModal";
import paymentLoadingGif from "../../../assets/gifs/loading.gif";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import CreditCardFormModal from "../../../components/dejavoo/cardDetailsModal";
import {LucideCardSim, WalletCardsIcon} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {selectAccount} from "../../../redux/accountSlice";
import {createPaymentTransaction, createPOSPaymentTransaction} from "../../../redux/paymentTransactionSlice";
import {toast} from "react-toastify";
import check from "app/assets/icons/check.png";
import {selectClients} from "../../../redux/clientsSlice";
import {
    CreditCard,
    Banknote,
    Keyboard,
    WalletCards,
} from "lucide-react";
import {getSalePaymentBalances} from "../../../utils/saleBalances";

export interface DualPricingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    creditCardPrice: string;
    cashPrice: string;
    onPayBy: (method: DualPricingPaymentTypes) => void;
}

const PAYMENT_OPTIONS = [
    {label: "Card", value: "CREDIT_CARD"},
    {label: "Cash", value: "CASH"},
    // { label: "Card On File", value: "CARD_ON_FILE" },
    {label: "Manual Entry", value: "MANUAL_ENTRY"},
] as const;

const ICON_SIZE = 40;

const renderIcon = (option: CardOption) => {
    const commonProps = {
        size: ICON_SIZE,
        className: "text-white mb-2",
        strokeWidth: 1.75,
    };

    switch (option) {
        case "CREDIT_CARD":
            return <CreditCard {...commonProps} />;
        case "CASH":
            return <Banknote {...commonProps} />;
        case "MANUAL_ENTRY":
            return <Keyboard {...commonProps} />;
        case "CARD_ON_FILE":
            return <WalletCards {...commonProps} />;
        default:
            return null;
    }
};


type CardOption = "CREDIT_CARD" | "CASH" | "CARD_ON_FILE" | "MANUAL_ENTRY" | null;


const PaymentModal: React.FC<any> = (props) => {
    const dispatch = useDispatch();

    const account = useSelector(selectAccount);
    const {clients} = useSelector(selectClients);
    const {open, onOpenChange, updateCalendarData, sale, tax, discount, appointmentClient} = props;
    console.log("sale", sale);

    const client = appointmentClient
        || clients?.find((cl) => cl?.id === sale?.clientId)
        || sale?.client;
    // The API sends has_card_on_file instead of the card token (charging a
    // card on file reads the token server-side); ccToken is the fallback for
    // API builds from before that change.
    const hasCardOnFile = client?.hasCardOnFile ?? !!(client?.ccToken || client?.cc_token);

    const [selectedOption, setSelectedOption] = useState<{ option: CardOption, selected: boolean } | null>()
    const [cardData, setCardData] = useState<any>({})

    const [success, setSuccess] = useState(false)


    const [paymentLoading, setPaymentLoading] = useState(false)

    // Same steps as Vino POS: Card on File goes through Add Tip, Cash through
    // the Cash Payment step (whose "No change due" turns the overpayment into
    // a tip), Card is tipped on the terminal and Manual Entry has no tip.
    // tipFor is the method waiting on one of those steps; the tip is kept in a
    // ref so the payment handlers read the value just chosen.
    const [tipFor, setTipFor] = useState<CardOption>(null)
    const tipCentsRef = useRef(0)


    const subtotalCents = sale?.subtotalCents ?? 0;
    const taxCents = Math.round(tax * 100) ?? sale?.taxCents;
    const discountCents = Math.round(discount*100) ?? sale?.discountCents;
    // The panel's live numbers are the truth - they include any discount
    // staff apply at checkout. (A stored sale total, e.g. from an online
    // booking, predates that discount, so charging it ignored the discount.)
    const computedFullCents = (subtotalCents) + taxCents - discountCents;
    const { paidCents, paidBaseCents, hasPriorPayment } =
        getSalePaymentBalances(sale);
    // Owed before any card fee: live total minus what earlier payments
    // covered (their card fees netted out).
    const chargeBaseCents = Math.max(0, computedFullCents - paidBaseCents);
    // The card fee only applies with dual pricing on - same rule as the
    // booking site, so a stored percentage alone doesn't charge it.
    const cardFeePercentage = account?.dualPricingEnabled ? (account?.cardFeePercentage ?? 0) : 0;

    // chargeBaseCents is owed before any card fee (paidBaseCents nets the
    // fee out of earlier card payments), so a balance after a deposit gets
    // the card fee too - matching the pending amount the booking site showed.
    const cardSurchargeCents = Math.round((chargeBaseCents * cardFeePercentage) / 100);

    const cardTotalCents = chargeBaseCents + cardSurchargeCents;
    const cashTotalCents = chargeBaseCents;

    const formatCurrency = (cents: number) =>
        `$${(cents / 100).toFixed(2)}`;


    const handleCompletePayment = useCallback(() => {
        setCardData(null);
        setSuccess(true);
        setTimeout(() => {
            setSelectedOption(null);
            setSuccess(false);
            onOpenChange(false); // closes PaymentModal
            props.onPaymentSuccess?.();
        }, 2500);

    }, []);

    const handleCloseCardModal = useCallback(() => {
        setSelectedOption(null)
        setCardData(null);
    }, [selectedOption]);


    const handlePaymentSelect = (paymentOption: CardOption) => {
        if (paymentOption === 'CARD_ON_FILE' || paymentOption === 'CASH') {
            setTipFor(paymentOption)
            return
        }
        // Card: the customer tips on the terminal, and the API records the
        // tip from the terminal's reply. Manual Entry: no tip, as in Vino.
        tipCentsRef.current = 0
        return runPayment(paymentOption)
    };

    const handleTipConfirm = (tipCents: number) => {
        const option = tipFor
        tipCentsRef.current = tipCents
        setTipFor(null)
        return runPayment(option)
    };

    const runPayment = async (paymentOption: CardOption) => {
        setSelectedOption({option: paymentOption, selected: true});


        if (paymentOption === 'MANUAL_ENTRY') {
            // card form is rendered via selectedOption
        }
        if (paymentOption === 'CREDIT_CARD') {
            // Card is the physical iPOS (SPIN) terminal. A saved card is
            // charged via Card on File, a typed card via Manual Entry.
            await handlePOS(sale)
        }
        if (paymentOption === 'CASH') {
            await handleCashCheckOut(sale)
        }
        if (paymentOption === 'CARD_ON_FILE') {
            await handleCardOnFileCheckOut(sale)
        }

    };


    const handleAfterValidation = async (res: any) => {
        console.log('validated', res)


        if (res.payment_token_id) {
            setCardData(res);
           await handleCardCheckOut(sale,res.payment_token_id)
        } else {
            console.log('errored-', res)
        }
    }

    const renderPaymentStep = useMemo(() => {
        switch (selectedOption?.option) {
            case "MANUAL_ENTRY":
                return (
                    <CreditCardFormModal
                        secretKey={account?.iposAuthToken}
                        onClose={() => {
                            setSelectedOption(null)
                        }}
                        onCardValidationSuccess={handleAfterValidation}
                        freedomURL={`${process.env.REACT_APP_IPOS_FREEDOM_CREDIT_URL!}`}
                    />
                );
                break;
            default:
                return <></>;
        }
    }, [selectedOption, handleAfterValidation]);


    const handleCardCheckOut = async (sale: any,token:string) => {
        try {
            setPaymentLoading(true)
            console.log('cardData on checkout', cardData);

            if (!sale) return;

            await dispatch(
                createPaymentTransaction({
                    subtotalCents: sale.subtotalCents,
                    taxCents,
                    discountCents,
                    feeType: "PERCENTAGE",
                    feeBasisPoint: cardFeePercentage * 100,
                    totalCents: cardTotalCents + tipCentsRef.current,
                    tipsCents: tipCentsRef.current,
                    saleId: sale.id,
                    paymentMethod: 'CARD',
                    voidPreviousPayments: false,
                    withExistingCard: false,
                    cardToken: token,
                }) as any
            ).unwrap();
            // setAppointmentDrawer({ isOpen: false, type: null })
            toast.success(`Payment done successfully`);
            handleCompletePayment()
            setPaymentLoading(false)
            await updateCalendarData()
        } catch (error: any) {
            setPaymentLoading(false)
            setSelectedOption(null)
            console.error('Payment failed:', error);
            toast.error(error?.message);
        }
    };


    const handleCashCheckOut = async (sale: any) => {
        setPaymentLoading(true)
        try {

            if (!sale) return;

            await dispatch(
                createPaymentTransaction({
                    subtotalCents: sale.subtotalCents,
                    taxCents,
                    discountCents,
                    totalCents: cashTotalCents + tipCentsRef.current,
                    tipsCents: tipCentsRef.current,
                    saleId: sale.id,
                    paymentMethod: 'CASH',
                    voidPreviousPayments: false,
                }) as any
            ).unwrap();
            setPaymentLoading(false)
            // setAppointmentDrawer({ isOpen: false, type: null })
            toast.success(`Payment done successfully`);
            await updateCalendarData()
            handleCompletePayment()




        } catch (error: any) {
            setPaymentLoading(false)
            setSelectedOption(null)
            console.error('Payment failed:', error);
            toast.error(error?.message);
        }

    };
    const handleCardOnFileCheckOut = async (sale: any) => {
        console.log('clicked card on file')
        setPaymentLoading(true)
        try {

            if (!sale) return;


            await dispatch(
                createPaymentTransaction({
                    subtotalCents: sale.subtotalCents,
                    taxCents,
                    discountCents,
                    feeType: "PERCENTAGE",
                    feeBasisPoint: cardFeePercentage * 100,
                    totalCents: cardTotalCents + tipCentsRef.current,
                    tipsCents: tipCentsRef.current,
                    saleId: sale.id,
                    paymentMethod: 'CARD',
                    voidPreviousPayments: false,
                    // The backend charges the client's saved card token itself.
                    withExistingCard: true,
                }) as any
            ).unwrap();
            // setAppointmentDrawer({ isOpen: false, type: null })
            toast.success(`Payment done successfully`);
            handleCompletePayment()
            await updateCalendarData()
            setPaymentLoading(false)
        } catch (error: any) {
            setPaymentLoading(false)
            setSelectedOption(null)
            console.error('Payment failed:', error);
            toast.error(error?.message ?? "Payment Failed");
        }

    };


    const handlePOS = useCallback(async (sale: any) => {
        if (!sale) return;
        setPaymentLoading(true)
        try {
            await dispatch(
                createPOSPaymentTransaction({
                    subtotalCents: sale.subtotalCents,
                    taxCents: taxCents ?? 0,
                    discountCents: discountCents ?? 0,
                    feeType: "PERCENTAGE",
                    feeBasisPoint: cardFeePercentage * 100,
                    totalCents: cardTotalCents,
                    // A tip is added on the terminal; the API reads it from the reply.
                    tipsCents: 0,
                    saleId: sale.id,
                }) as any
            ).unwrap()

            await updateCalendarData()
            // {
            //     view === 'week' ? await getWeeklySchedulesOfEmployees({employeeId: +employeeId!, date: new Date(date!)}) : await getSchedulesOfEmployees({specificEmployeeId: undefined, currentDate: new Date(date!)})
            // }

            toast.success("Payment done successfully");
            setPaymentLoading(false)
            handleCompletePayment();
        } catch (error: any) {
            setSelectedOption(null)
            console.error("Payment failed:", error);
            // e.g. "Declined" or "Terminal not connected to SPIn Proxy server"
            toast.error(error?.message ?? "Payment failed. Please try again.");
        } finally {
            setPaymentLoading(false)
        }
    // Include the amounts: with only [dispatch, sale, cardData] the terminal
    // was sent the totals from when the modal opened (e.g. without a
    // discount added afterwards).
    }, [dispatch, taxCents, discountCents, cardFeePercentage, cardTotalCents, updateCalendarData, handleCompletePayment]);


    useEffect(() => {
        console.log('open state', open, selectedOption)
        console.log('client', client)
        console.log('sale', sale)


    }, [open]);


    return (
        <FadeInSlide direction="bottom" className="absolute">
            <DialogLayout
                open={open}
                onOpenChange={(e) => {
                    setSelectedOption(null)
                    setTipFor(null)
                    onOpenChange(e)
                }}
                className="z-[10000]"
            >
                <div
                    className="relative flex h-144 w-192 flex-col items-center gap-4 rounded-lg bg-default-background px-6 pt-6 pb-10 shadow-dialog-shadow mobile:h-auto mobile:max-h-[90dvh] mobile:overflow-y-auto mobile:w-[calc(100vw-1.5rem)] mobile:max-w-96 mobile:px-2 mobile:pt-2 mobile:pb-4">
                    <div className="flex w-full items-start justify-end gap-6">
                        <div className="flex grow shrink-0 basis-0 flex-col items-start justify-center gap-2">
              <span className="text-body-bold font-body-bold text-neutral-900">
              Choose a payment method
              </span>
                            {hasPriorPayment && (
                                <span className="text-sm text-neutral-600">
                                    Paid {formatCurrency(paidCents)} · Due {formatCurrency(chargeBaseCents)}
                                </span>
                            )}

                        </div>
                        <AsIconButton
                            className="h-8 w-8 flex-none"
                            variant="ghost"
                            leadingIcon="FeatherX"
                            trailingIcon={null}
                            text="Label"
                            size="default"
                            disabled={false}
                            onClick={() => {
                                setSelectedOption(null)
                                setTipFor(null)
                                onOpenChange(false)
                            }}
                        />
                    </div>


                    <div className={'w-full p-4'}>

                        {paymentLoading && <div className={'flex-col flex justify-center items-center'}>
                            <img src={paymentLoadingGif} width={100} height={100} alt={'loading'}/>
                            <p>Waiting for Payment Completion</p>

                        </div>}

                        {success && <div className={'w-full flex flex-col justify-center items-center'}>
                            <h1 className={'text-lg'}>Payment Successful</h1>
                            <img width={100} height={100} src={check} alt={'completed'}/>

                        </div>}


                        <div className="w-full grid grid-cols-2 ">
                            {/*{!success && !selectedOption?.selected && !paymentLoading && PAYMENT_OPTIONS?.map((opt) => (*/}
                            {/*    <button*/}
                            {/*        key={opt.value}*/}
                            {/*        onClick={() => handlePaymentSelect(opt?.value)}*/}
                            {/*        className={` gird grid-cols-2 place-items-center h-48 items-center justify-center p-4 border-2 border-blue-200 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition`}*/}
                            {/*    >*/}
                            {/*        {renderIcon(opt.value)}*/}
                            {/*        {opt.label}*/}
                            {/*    </button>*/}

                            {/*))}*/}
                            {/*{!success && !selectedOption?.selected && !paymentLoading && <button*/}
                            {/*    disabled={!client?.ccToken}*/}
                            {/*    key={'card_on_file'}*/}
                            {/*    onClick={() => handlePaymentSelect('CARD_ON_FILE')}*/}
                            {/*    className={`disabled:bg-gray-800 gird grid-cols-2 place-items-center h-48 items-center justify-center p-4 border-2 border-blue-200 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition`}*/}
                            {/*>*/}
                            {/*    {renderIcon('CARD_ON_FILE')}*/}

                            {/*    <span className={'flex flex-col items-center'}>*/}
                            {/*        <p>  Card On File</p>*/}
                            {/*        <p className={'text-gray-300 text-sm'}>{client?.ccToken ? 'Available' : 'Not Available'}</p>*/}

                            {/*    </span>*/}


                            {/*</button>}*/}



                            {!success && !selectedOption?.selected && !paymentLoading &&
                                PAYMENT_OPTIONS.map((opt) => {
                                    const isCard =
                                        opt.value === "CREDIT_CARD" || opt.value === "MANUAL_ENTRY";
                                    const isCash = opt.value === "CASH";

                                    const totalToShow = isCard
                                        ? cardTotalCents
                                        : isCash
                                            ? cashTotalCents
                                            : null;

                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => handlePaymentSelect(opt.value)}
                                            className="flex flex-col items-center justify-center h-48 p-4 border-2 border-blue-200 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                                        >
                                            {renderIcon(opt.value)}

                                            <span className="text-base font-semibold">
          {opt.label}
        </span>

                                            {(isCard || isCash) && (
                                                <div className="mt-2 text-sm text-blue-100 text-center">
                                                    <p>{formatCurrency(totalToShow!)}</p>

                                                    {isCard && cardFeePercentage > 0 && (
                                                        <p className="text-xs text-blue-200">
                                                            Includes {cardFeePercentage}% card fee
                                                        </p>
                                                    )}

                                                    {isCash && (
                                                        <p className="text-xs text-blue-200">
                                                            No processing fee
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            {!success && !selectedOption?.selected && !paymentLoading && (
                                <button
                                    disabled={!hasCardOnFile}
                                    onClick={() => handlePaymentSelect("CARD_ON_FILE")}
                                    className="flex flex-col items-center justify-center h-48 p-4 border-2 border-blue-200 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-700 transition"
                                >
                                    {renderIcon("CARD_ON_FILE")}

                                    <span className="text-base font-semibold">
      Card on File
    </span>

                                    <p className="text-sm mt-1">
                                        {formatCurrency(cardTotalCents)}
                                    </p>

                                    {cardFeePercentage > 0 && (
                                        <p className="text-xs text-blue-200">
                                            Includes {cardFeePercentage}% card fee
                                        </p>
                                    )}

                                    {!hasCardOnFile && (
                                        <p className="text-xs text-gray-300 mt-1">
                                            Not available
                                        </p>
                                    )}
                                </button>
                            )}




                        </div>

                        <div>
                            {!success && renderPaymentStep}

                        </div>

                    </div>

                    <TipModal
                        isOpen={tipFor === 'CARD_ON_FILE'}
                        priceCents={cardTotalCents}
                        priceLabel="Card Price"
                        clientName={client?.name}
                        onClose={() => setTipFor(null)}
                        onConfirm={handleTipConfirm}
                    />

                    <CashTenderedModal
                        isOpen={tipFor === 'CASH'}
                        amountDue={cashTotalCents / 100}
                        onClose={() => setTipFor(null)}
                        onConfirm={(_tendered, _change, tip) => handleTipConfirm(Math.round(tip * 100))}
                    />


                </div>


            </DialogLayout>
        </FadeInSlide>
    );
};
export default PaymentModal
